import axios, { AxiosResponse } from 'axios';

function isLocalDevelopment(): boolean {
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}

// ─── Base Instance ─────────────────────────────────────────────────────────────
const axiosInstance = axios.create({
  baseURL: '/server/sample_node_js_function',
  timeout: 10000,
  withCredentials: true,
});

// ─── Request Interceptor ───────────────────────────────────────────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    // Attach auth token here if needed in future
    // config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor ─────────────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    let message =
      'A small error has occurred, causing an interruption of service. Please try again.';

    console.error('API Error:\n', error);

    if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
      message =
        "We're having trouble connecting to the network. Please try again later.";
      return Promise.reject(new Error(message));
    }

    if (error && error.response) {
      const errResp: AxiosResponse = error.response;

      if (errResp && errResp.data) {
        // Extract server-provided message if available
        if (errResp.data.message) {
          message = errResp.data.message;
        } else if (errResp.data.error) {
          message = errResp.data.error;
        }

        // Handle session expiry (440)
        if (
          !isLocalDevelopment() &&
          (errResp.status === 401 ||
            errResp.status === 440 ||
            errResp.data.statusCode === 440)
        ) {
          console.warn('Session expired. Redirecting to login...');
          setTimeout(() => {
            window.location.assign('/__catalyst/auth/login');
          }, 1500);
        }

        // Handle maintenance mode (503)
        if (errResp.data.statusCode === 503) {
          console.warn('Service unavailable. Redirecting...');
          setTimeout(() => {
            window.location.href = '/under-maintenance';
          }, 1500);
        }
      }
    }

    return Promise.reject(new Error(message));
  }
);

export type ProjectEventType =
  | 'created'
  | 'stage'
  | 'health'
  | 'archived'
  | 'issue'
  | 'document';

export interface ProjectHistoryEntry {
  type: ProjectEventType;
  from?: string;
  to?: string;
  note?: string;
  actor?: string;
  at?: string;
}

export type QualitySeverity = 'P1' | 'P2' | 'P3' | 'P4';
export type QualityStatus = 'Open' | 'Monitoring' | 'Ready For QC' | 'Closed';
export type DocumentType =
  | 'Award Document'
  | 'Drawing'
  | 'Material Approval'
  | 'Site Record'
  | 'Handover Pack'
  | 'Warranty Evidence';
export type DocumentStatus = 'Missing' | 'Requested' | 'Received' | 'Approved';

export interface QualityIssue {
  _id: string;
  title: string;
  description?: string;
  severity: QualitySeverity;
  status: QualityStatus;
  owner?: string;
  verifiedBy?: string;
  verifiedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectDocument {
  _id: string;
  title: string;
  type: DocumentType;
  status: DocumentStatus;
  owner?: string;
  reference?: string;
  lastUpdatedNote?: string;
  fileId?: string;
  fileFolderId?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  hasFile?: boolean;
  downloadUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkflowChecklistItem {
  done: boolean;
  label: string;
}

export interface WorkflowGateStatus {
  canAdvance: boolean;
  checklist: WorkflowChecklistItem[];
  nextStage: ProjectStage | null;
  reasons: string[];
}

export interface CatalystRow {
  _id: string;
  name: string;
  projectCode: string;
  client: string;
  owner: string;
  stage: ProjectStage;
  health: ProjectHealth;
  nextAction?: string;
  summary?: string;
  isArchived?: boolean;
  history?: ProjectHistoryEntry[];
  qualityIssues?: QualityIssue[];
  documents?: ProjectDocument[];
  workflow?: WorkflowGateStatus;
  workflowSummary?: {
    documentCoverage: {
      approved: number;
      pending: number;
      total: number;
    };
    openIssues: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

export type ProjectRole = 'admin' | 'viewer';

export interface SessionInfo {
  email: string;
  role: ProjectRole;
  canManage: boolean;
}

export type ProjectStage =
  | 'Awarded'
  | 'Mobilisation'
  | 'Execution'
  | 'Snagging'
  | 'Handover'
  | 'Warranty';

export type ProjectHealth = 'On Track' | 'Watch' | 'Blocked';
export type ProjectStageFilter = ProjectStage | 'all';
export type ProjectHealthFilter = ProjectHealth | 'all';
export type ProjectSortField =
  | 'updatedAt'
  | 'createdAt'
  | 'name'
  | 'projectCode'
  | 'client'
  | 'owner'
  | 'stage'
  | 'health';
export type ProjectSortDirection = 'asc' | 'desc';

export const PROJECT_STAGES: ProjectStage[] = [
  'Awarded',
  'Mobilisation',
  'Execution',
  'Snagging',
  'Handover',
  'Warranty',
];

export const PROJECT_HEALTHS: ProjectHealth[] = ['On Track', 'Watch', 'Blocked'];
export const QUALITY_SEVERITIES: QualitySeverity[] = ['P1', 'P2', 'P3', 'P4'];
export const QUALITY_STATUSES: QualityStatus[] = [
  'Open',
  'Monitoring',
  'Ready For QC',
  'Closed',
];
export const DOCUMENT_TYPES: DocumentType[] = [
  'Award Document',
  'Drawing',
  'Material Approval',
  'Site Record',
  'Handover Pack',
  'Warranty Evidence',
];
export const DOCUMENT_STATUSES: DocumentStatus[] = [
  'Missing',
  'Requested',
  'Received',
  'Approved',
];

export interface ProjectListQuery {
  page?: number;
  limit?: number;
  search?: string;
  stage?: ProjectStageFilter;
  health?: ProjectHealthFilter;
  sortBy?: ProjectSortField;
  sortOrder?: ProjectSortDirection;
}

export interface ProjectPagination {
  page: number;
  limit: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ProjectSummaryCounts<T extends string> {
  totalRecords: number;
  stageCounts?: Record<T, number>;
  healthCounts?: Record<T, number>;
}

export interface ProjectListResponse {
  data: CatalystRow[];
  dashboard: {
    totalProjects: number;
    blockedProjects: number;
    watchProjects: number;
    warrantyProjects: number;
    openIssues: number;
    criticalIssues: number;
    pendingDocuments: number;
    projectsNeedingAction: number;
  };
  session: SessionInfo;
  filters: {
    search: string;
    stage: null | ProjectStage;
    health: null | ProjectHealth;
    sortBy: ProjectSortField;
    sortOrder: ProjectSortDirection;
  };
  pagination: ProjectPagination;
  summary: {
    filtered: {
      totalRecords: number;
      stageCounts: Record<ProjectStage, number>;
      healthCounts: Record<ProjectHealth, number>;
    };
    overall: {
      totalRecords: number;
      stageCounts: Record<ProjectStage, number>;
      healthCounts: Record<ProjectHealth, number>;
    };
  };
}

export interface CreateProjectPayload {
  name: string;
  client: string;
  owner: string;
  stage?: ProjectStage;
  health?: ProjectHealth;
  nextAction?: string;
  summary?: string;
}

export interface UpdateProjectPayload {
  name?: string;
  client?: string;
  owner?: string;
  stage?: ProjectStage;
  health?: ProjectHealth;
  nextAction?: string;
  summary?: string;
}

export interface TransitionProjectPayload {
  stage?: ProjectStage;
  health?: ProjectHealth;
  note?: string;
}

export interface ProjectIssuePayload {
  title: string;
  description?: string;
  severity?: QualitySeverity;
  status?: QualityStatus;
  owner?: string;
}

export interface ProjectDocumentPayload {
  title: string;
  type?: DocumentType;
  status?: DocumentStatus;
  owner?: string;
  reference?: string;
  lastUpdatedNote?: string;
  file?: File | null;
}

export interface ProjectMutationResponse {
  message: string;
  data: CatalystRow;
}

function buildProjectDocumentFormData(payload: ProjectDocumentPayload): FormData {
  const formData = new FormData();

  formData.append('title', payload.title);
  if (payload.type) {
    formData.append('type', payload.type);
  }
  if (payload.status) {
    formData.append('status', payload.status);
  }
  if (payload.owner !== undefined) {
    formData.append('owner', payload.owner);
  }
  if (payload.reference !== undefined) {
    formData.append('reference', payload.reference);
  }
  if (payload.lastUpdatedNote !== undefined) {
    formData.append('lastUpdatedNote', payload.lastUpdatedNote);
  }
  if (payload.file) {
    formData.append('file', payload.file);
  }

  return formData;
}

export const APIGetProjects = async (
  query: ProjectListQuery
): Promise<AxiosResponse<ProjectListResponse>> => {
  return axiosInstance.get('/projects', {
    params: {
      page: query.page,
      limit: query.limit,
      search: query.search || undefined,
      stage: query.stage && query.stage !== 'all' ? query.stage : undefined,
      health: query.health && query.health !== 'all' ? query.health : undefined,
      sortBy: query.sortBy,
      sortOrder: query.sortOrder,
    },
  });
};

export const APICreateProject = async (
  payload: CreateProjectPayload
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  return axiosInstance.post('/projects', payload);
};

export const APIUpdateProject = async (
  id: string,
  payload: UpdateProjectPayload
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  return axiosInstance.put(`/projects/${id}`, payload);
};

export const APIArchiveProject = async (
  id: string
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  return axiosInstance.delete(`/projects/${id}`);
};

export const APITransitionProject = async (
  id: string,
  payload: TransitionProjectPayload
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  return axiosInstance.post(`/projects/${id}/transition`, payload);
};

export const APICreateProjectIssue = async (
  projectId: string,
  payload: ProjectIssuePayload
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  return axiosInstance.post(`/projects/${projectId}/issues`, payload);
};

export const APIUpdateProjectIssue = async (
  projectId: string,
  issueId: string,
  payload: Partial<ProjectIssuePayload>
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  return axiosInstance.put(`/projects/${projectId}/issues/${issueId}`, payload);
};

export const APIDeleteProjectIssue = async (
  projectId: string,
  issueId: string
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  return axiosInstance.delete(`/projects/${projectId}/issues/${issueId}`);
};

export const APICreateProjectDocument = async (
  projectId: string,
  payload: ProjectDocumentPayload
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  return axiosInstance.post(
    `/projects/${projectId}/documents`,
    buildProjectDocumentFormData(payload)
  );
};

export const APIUpdateProjectDocument = async (
  projectId: string,
  documentId: string,
  payload: Partial<ProjectDocumentPayload>
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  const formData = buildProjectDocumentFormData({
    title: payload.title ?? '',
    type: payload.type,
    status: payload.status,
    owner: payload.owner,
    reference: payload.reference,
    lastUpdatedNote: payload.lastUpdatedNote,
    file: payload.file ?? null,
  });

  if (payload.title === undefined) {
    formData.delete('title');
  }

  return axiosInstance.put(`/projects/${projectId}/documents/${documentId}`, formData);
};

export const APIDeleteProjectDocument = async (
  projectId: string,
  documentId: string
): Promise<AxiosResponse<ProjectMutationResponse>> => {
  return axiosInstance.delete(`/projects/${projectId}/documents/${documentId}`);
};

export function getProjectDocumentDownloadUrl(
  projectId: string,
  document: ProjectDocument
): string {
  if (document.downloadUrl) {
    return document.downloadUrl;
  }

  return `/server/sample_node_js_function/projects/${projectId}/documents/${document._id}/download`;
}
